import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { notify } from '../utils/notify';
import { mensajeDeError } from '../utils/apiError';
import { notificationsAPI } from '../services/api';
import { HiBell, HiCheck, HiTrash, HiExternalLink } from 'react-icons/hi';
import { Link } from 'react-router-dom';

export default function NotificationsDropdown() {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    // La lista se recarga sola cada minuto. Un fallo ahi NO puede sacar un
    // modal —seria uno por minuto con el servidor caido— pero tampoco puede
    // verse como «no tienes notificaciones». Se dice dentro del panel.
    const [errorCarga, setErrorCarga] = useState(false);
    const dropdownRef = useRef(null);

    // Fetch notifications on mount and periodically
    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000); // Every minute
        return () => clearInterval(interval);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchNotifications = async () => {
        try {
            const response = await notificationsAPI.getAll({ limit: 10 });
            if (response.data.success) {
                setNotifications(response.data.data.notifications || []);
                setUnreadCount(response.data.data.unreadCount || 0);
                setErrorCarga(false);
            }
        } catch (error) {
            setErrorCarga(true);
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            await notificationsAPI.markAsRead(id);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            notify.error(mensajeDeError(error, t('notifications.mark_failed')));
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            setLoading(true);
            await notificationsAPI.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            notify.error(mensajeDeError(error, t('notifications.mark_failed')));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await notificationsAPI.delete(id);
            const deleted = notifications.find(n => n.id === id);
            setNotifications(prev => prev.filter(n => n.id !== id));
            if (deleted && !deleted.read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            notify.error(mensajeDeError(error, t('notifications.delete_failed')));
        }
    };

    const getNotificationIcon = (type) => {
        const icons = {
            order: '📦',
            product: '🏷️',
            store: '🏪',
            system: '⚙️',
            alert: '⚠️'
        };
        return icons[type] || '🔔';
    };

    const formatTime = (date) => {
        const now = new Date();
        const notifDate = new Date(date);
        const diffMs = now - notifDate;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return notifDate.toLocaleDateString();
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
                <HiBell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-danger-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{t('notifications.title')}</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllAsRead}
                                disabled={loading}
                                className="text-xs text-primary-500 hover:text-primary-600 font-medium disabled:opacity-50"
                            >
                                {t('notifications.mark_all_read')}
                            </button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="py-8 text-center text-slate-500">
                                <HiBell className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                                <p>{errorCarga ? t('common.load_failed_title') : t('notifications.empty')}</p>
                                {errorCarga && (
                                    <button
                                        type="button"
                                        onClick={fetchNotifications}
                                        className="btn-secondary btn-sm mt-3"
                                    >
                                        {t('common.retry')}
                                    </button>
                                )}
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`flex gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${!notification.read ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
                                        }`}
                                >
                                    <span className="text-xl flex-shrink-0">
                                        {getNotificationIcon(notification.type)}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm ${!notification.read ? 'font-semibold' : ''} text-slate-900 dark:text-white`}>
                                            {notification.title}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                            {notification.message}
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1">
                                            {formatTime(notification.created_at)}
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {!notification.read && (
                                            <button
                                                onClick={() => handleMarkAsRead(notification.id)}
                                                className="p-1 text-slate-400 hover:text-green-500 transition-colors"
                                                title={t('notifications.mark_as_read')}
                                            >
                                                <HiCheck className="w-4 h-4" />
                                            </button>
                                        )}
                                        {/* El servidor guarda el destino en
                                            `data.actionUrl`, no en una columna
                                            `action_url` —esa columna nunca ha
                                            existido en la tabla—. Aquí se leía
                                            el nombre equivocado, así que este
                                            enlace no se ha pintado jamás. */}
                                        {notification.data?.actionUrl && (
                                            <Link
                                                to={notification.data.actionUrl}
                                                onClick={() => setIsOpen(false)}
                                                className="icon-view !p-1"
                                                title={t('notifications.view')}
                                            >
                                                <HiExternalLink className="w-4 h-4" />
                                            </Link>
                                        )}
                                        <button
                                            onClick={() => handleDelete(notification.id)}
                                            className="icon-delete !p-1"
                                            title={t('common.delete')}
                                        >
                                            <HiTrash className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
